import React, { useState, useEffect, useRef, useCallback } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'
import {
  getUserTickets,
  createSupportTicket,
  getCurrentDbUserId,
  getUserType,
  getTicketResponses,
  createUserTicketResponse,
  countUserOpenTickets,
  uploadTicketAttachment,
  type SupportTicket,
  type TicketResponse,
  type TicketAttachment
} from '../../lib/supportSystem'

const MAX_OPEN_TICKETS = 5
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']

// v2.9.55: Consolidated 5 department categories
const CATEGORY_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  general_support: { label: 'General Support', icon: '💬', color: 'from-blue-600 to-blue-800 border-blue-500' },
  account_finance: { label: 'Account / Finance', icon: '💰', color: 'from-green-600 to-green-800 border-green-500' },
  technical_faults: { label: 'Technical / Faults', icon: '🔧', color: 'from-red-600 to-red-800 border-red-500' },
  user_data_profile: { label: 'User Data / Profile', icon: '👤', color: 'from-purple-600 to-purple-800 border-purple-500' },
  commissions_referrals: { label: 'Commissions / Referrals', icon: '🤝', color: 'from-teal-600 to-teal-800 border-teal-500' },
  // Legacy categories (for displaying existing tickets — no longer selectable by users)
  general: { label: 'General Support', icon: '💬', color: 'from-blue-600 to-blue-800 border-blue-500' },
  technical: { label: 'Technical Issue', icon: '🔧', color: 'from-red-600 to-red-800 border-red-500' },
  billing: { label: 'Billing Question', icon: '💰', color: 'from-green-600 to-green-800 border-green-500' },
  bank_payment: { label: 'Bank Payment', icon: '🏦', color: 'from-indigo-600 to-indigo-800 border-indigo-500' },
  crypto_payment: { label: 'Crypto Payment', icon: '₿', color: 'from-orange-600 to-orange-800 border-orange-500' },
  investment: { label: 'Share Purchase Question', icon: '📈', color: 'from-emerald-600 to-emerald-800 border-emerald-500' },
  portfolio: { label: 'Portfolio Management', icon: '📊', color: 'from-cyan-600 to-cyan-800 border-cyan-500' },
  dividends: { label: 'Account / Finance', icon: '💰', color: 'from-green-600 to-green-800 border-green-500' },
  commission: { label: 'Commission Question', icon: '🤝', color: 'from-teal-600 to-teal-800 border-teal-500' },
  marketing: { label: 'Marketing Support', icon: '📣', color: 'from-pink-600 to-pink-800 border-pink-500' },
  referrals: { label: 'Referral System', icon: '👥', color: 'from-violet-600 to-violet-800 border-violet-500' },
  account: { label: 'Account Issue', icon: '👤', color: 'from-slate-600 to-slate-800 border-slate-500' },
  security: { label: 'Security Concern', icon: '🔒', color: 'from-amber-600 to-amber-800 border-amber-500' },
  kyc: { label: 'KYC / Verification', icon: '✅', color: 'from-lime-600 to-lime-800 border-lime-500' },
  other: { label: 'Other', icon: '📝', color: 'from-gray-600 to-gray-800 border-gray-500' },
}

const formatCategoryLabel = (category: string): string => {
  return CATEGORY_CONFIG[category]?.label || category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

const getCategoryIcon = (category: string): string => {
  return CATEGORY_CONFIG[category]?.icon || '📝'
}

// Toast notification types
type ToastType = 'error' | 'success' | 'warning' | 'info'
interface ToastMessage {
  id: number
  type: ToastType
  message: string
}

let toastIdCounter = 0

interface TicketingSystemProps {
  userId?: number
}

const TicketingSystem: React.FC<TicketingSystemProps> = ({ userId: propUserId }) => {
  const [tickets, setTickets] = useState<SupportTicket[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [_userType, setUserType] = useState<'shareholder' | 'affiliate'>('shareholder')
  const [userId, setUserId] = useState<number | null>(propUserId || null)
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null)
  const [openTicketCount, setOpenTicketCount] = useState(0)
  const [initError, setInitError] = useState<string | null>(null)
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  // Toast notification helper
  const showToast = useCallback((type: ToastType, message: string) => {
    const id = ++toastIdCounter
    setToasts(prev => [...prev, { id, type, message }])
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, 5000)
  }, [])

  const dismissToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Ticket responses state
  const [responses, setResponses] = useState<TicketResponse[]>([])
  const [loadingResponses, setLoadingResponses] = useState(false)
  const [replyMessage, setReplyMessage] = useState('')
  const [replyAttachments, setReplyAttachments] = useState<File[]>([])
  const [sendingReply, setSendingReply] = useState(false)
  const replyFileInputRef = useRef<HTMLInputElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Create ticket form state
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent'
  })
  const [newTicketAttachments, setNewTicketAttachments] = useState<File[]>([])
  const [csatRating, setCsatRating] = useState(0)
  const [csatFeedback, setCsatFeedback] = useState('')
  const [csatSubmitting, setCsatSubmitting] = useState(false)
  const [csatSubmitted, setCsatSubmitted] = useState(false)
  const createFileInputRef = useRef<HTMLInputElement>(null)

  // Track object URLs for cleanup to prevent memory leaks
  const [newTicketPreviewUrls, setNewTicketPreviewUrls] = useState<string[]>([])
  const [replyPreviewUrls, setReplyPreviewUrls] = useState<string[]>([])

  // Generate stable preview URLs when attachments change
  useEffect(() => {
    // Revoke old URLs
    newTicketPreviewUrls.forEach(url => URL.revokeObjectURL(url))
    // Create new URLs
    const urls = newTicketAttachments.map(file => URL.createObjectURL(file))
    setNewTicketPreviewUrls(urls)
    return () => { urls.forEach(url => URL.revokeObjectURL(url)) }
  }, [newTicketAttachments])

  useEffect(() => {
    replyPreviewUrls.forEach(url => URL.revokeObjectURL(url))
    const urls = replyAttachments.map(file => URL.createObjectURL(file))
    setReplyPreviewUrls(urls)
    return () => { urls.forEach(url => URL.revokeObjectURL(url)) }
  }, [replyAttachments])

  useEffect(() => {
    initializeTicketing()
  }, [propUserId])

  // Load responses when a ticket is selected
  useEffect(() => {
    if (selectedTicket) {
      loadTicketResponses(selectedTicket.id)
    }
  }, [selectedTicket])

  // Scroll to bottom when responses change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [responses])

  const initializeTicketing = async () => {
    setLoading(true)
    setInitError(null)
    try {
      let currentUserId: number | null | undefined = propUserId
      if (!currentUserId) {
        console.log('🎫 [Ticketing] No propUserId, calling getCurrentDbUserId...')
        currentUserId = await getCurrentDbUserId()
        console.log('🎫 [Ticketing] getCurrentDbUserId returned:', currentUserId)
      }

      if (!currentUserId) {
        console.error('🎫 [Ticketing] User not logged in — no userId available')
        setInitError('Unable to identify your account. Please log out and log back in.')
        return
      }

      setUserId(currentUserId)
      console.log('🎫 [Ticketing] Fetching user type for userId:', currentUserId)
      const type = await getUserType(currentUserId)
      console.log('🎫 [Ticketing] User type:', type)
      setUserType(type)

      await loadTickets(currentUserId)

      // Load open ticket count
      const count = await countUserOpenTickets(currentUserId)
      setOpenTicketCount(count)
      console.log('🎫 [Ticketing] Init complete. Tickets loaded, open count:', count)
    } catch (error: any) {
      console.error('🎫 [Ticketing] Error initializing ticketing:', error)
      setInitError(error?.message || 'Failed to load support tickets. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const loadTickets = async (currentUserId: number) => {
    try {
      const userTickets = await getUserTickets(currentUserId)
      setTickets(userTickets)
    } catch (error) {
      console.error('Error loading tickets:', error)
    }
  }

  const loadTicketResponses = async (ticketId: string | number) => {
    setLoadingResponses(true)
    try {
      const ticketResponses = await getTicketResponses(ticketId)
      setResponses(ticketResponses)
    } catch (error) {
      console.error('Error loading ticket responses:', error)
    } finally {
      setLoadingResponses(false)
    }
  }

  // Handle file selection for new ticket
  const handleNewTicketFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const validFiles = files.filter(file => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        showToast('error', `Invalid file type: ${file.name}. Only JPEG, PNG, GIF, WebP images, and PDF documents are allowed.`)
        return false
      }
      if (file.size > MAX_FILE_SIZE) {
        showToast('error', `File too large: ${file.name}. Maximum size is 5MB.`)
        return false
      }
      return true
    })
    setNewTicketAttachments(prev => [...prev, ...validFiles])
    if (createFileInputRef.current) createFileInputRef.current.value = ''
  }

  // Handle file selection for reply
  const handleReplyFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const validFiles = files.filter(file => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        showToast('error', `Invalid file type: ${file.name}. Only JPEG, PNG, GIF, WebP images, and PDF documents are allowed.`)
        return false
      }
      if (file.size > MAX_FILE_SIZE) {
        showToast('error', `File too large: ${file.name}. Maximum size is 5MB.`)
        return false
      }
      return true
    })
    setReplyAttachments(prev => [...prev, ...validFiles])
    if (replyFileInputRef.current) replyFileInputRef.current.value = ''
  }

  // Remove attachment from new ticket
  const removeNewTicketAttachment = (index: number) => {
    setNewTicketAttachments(prev => prev.filter((_, i) => i !== index))
  }

  // Remove attachment from reply
  const removeReplyAttachment = (index: number) => {
    setReplyAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const handleCreateTicket = async () => {
    if (!userId || !newTicket.title.trim()) return

    // Check open ticket limit
    if (openTicketCount >= MAX_OPEN_TICKETS) {
      showToast('warning', `You have reached the maximum of ${MAX_OPEN_TICKETS} open tickets. Please wait for existing tickets to be resolved before creating new ones.`)
      return
    }

    // Payment categories require description and proof of payment
    const isPaymentCategory = newTicket.category === 'bank_payment' || newTicket.category === 'crypto_payment'
    if (isPaymentCategory) {
      if (!newTicket.description.trim()) {
        showToast('warning', 'Please provide the required payment details in the description field.')
        return
      }
      if (newTicketAttachments.length === 0) {
        showToast('warning', 'Please upload proof of payment for payment-related issues.')
        return
      }
    }

    setLoading(true)
    try {
      const ticket = await createSupportTicket(
        userId,
        newTicket.title,
        newTicket.description,
        newTicket.category || 'general',
        newTicket.priority
      )

      if (ticket) {
        // Upload attachments if any
        if (newTicketAttachments.length > 0) {
          const uploadedAttachments: TicketAttachment[] = []
          for (const file of newTicketAttachments) {
            const attachment = await uploadTicketAttachment(file, ticket.id, userId)
            if (attachment) {
              uploadedAttachments.push(attachment)
            }
          }

          // If we have attachments, create an initial response with them
          if (uploadedAttachments.length > 0) {
            await createUserTicketResponse(
              ticket.id,
              userId,
              'Attached images:',
              uploadedAttachments
            )
          }
        }

        setTickets(prev => [ticket, ...prev])
        setNewTicket({ title: '', description: '', category: '', priority: 'medium' })
        setNewTicketAttachments([])
        setShowCreateForm(false)
        setOpenTicketCount(prev => prev + 1)
      }
    } catch (error) {
      console.error('Error creating ticket:', error)
      showToast('error', 'Failed to create ticket. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Handle sending a reply
  const handleSendReply = async () => {
    if (!userId || !selectedTicket || (!replyMessage.trim() && replyAttachments.length === 0)) return

    setSendingReply(true)
    try {
      // Upload attachments first
      const uploadedAttachments: TicketAttachment[] = []
      for (const file of replyAttachments) {
        const attachment = await uploadTicketAttachment(file, selectedTicket.id, userId)
        if (attachment) {
          uploadedAttachments.push(attachment)
        }
      }

      // Create the response
      const response = await createUserTicketResponse(
        selectedTicket.id,
        userId,
        replyMessage.trim() || 'Attached images:',
        uploadedAttachments
      )

      if (response) {
        // Reload responses
        await loadTicketResponses(selectedTicket.id)
        setReplyMessage('')
        setReplyAttachments([])
      }
    } catch (error) {
      console.error('Error sending reply:', error)
      showToast('error', 'Failed to send reply. Please try again.')
    } finally {
      setSendingReply(false)
    }
  }

  const submitCSAT = async () => {
    if (!selectedTicket || csatRating === 0) return
    setCsatSubmitting(true)
    try {
      const res = await fetch('/api/user/support-system', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'submitCSAT', data: { ticketId: selectedTicket.id, rating: csatRating, feedback: csatFeedback.trim() || null } })
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error)
      setCsatSubmitted(true)
    } catch (err) {
      console.error('CSAT submit failed:', err)
      showToast('error', 'Failed to submit feedback. Please try again.')
    }
    setCsatSubmitting(false)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800'
      case 'in_progress': return 'bg-yellow-100 text-yellow-800'
      case 'waiting_user': return 'bg-orange-100 text-orange-800'
      case 'resolved': return 'bg-green-100 text-green-800'
      case 'closed': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (initError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <div className="text-red-400 text-lg font-semibold">⚠️ Support System Error</div>
        <p className="text-white text-center max-w-md">{initError}</p>
        <button
          onClick={() => initializeTicketing()}
          className="bg-yellow-600 hover:bg-yellow-500 text-white font-medium py-2 px-4 rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  if (loading && tickets.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Professional Header */}
      <div className="bg-gradient-to-r from-gray-800 via-gray-800 to-gray-900 rounded-2xl border border-gray-700 p-6 shadow-xl">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white tracking-tight">Support Center</h2>
              <p className="text-gray-400 text-sm mt-0.5">
                Create tickets, track progress, and get help from our team
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {openTicketCount > 0 && (
              <div className="flex items-center gap-2 bg-blue-900/40 border border-blue-700 rounded-lg px-3 py-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                <span className="text-sm text-blue-300 font-medium">{openTicketCount} Open</span>
              </div>
            )}
            <button
              onClick={() => {
                setNewTicket({ title: '', description: '', category: '', priority: 'medium' })
                setNewTicketAttachments([])
                setShowCreateForm(true)
              }}
              className="bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 text-white font-semibold py-2.5 px-6 rounded-xl transition-all shadow-lg hover:shadow-yellow-900/30 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Ticket
            </button>
          </div>
        </div>
      </div>

      {/* Create Ticket Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-b from-gray-900 to-gray-950 border border-gray-700 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="p-5 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-gray-900/95 backdrop-blur rounded-t-2xl z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">New Support Ticket</h3>
                  <p className="text-xs text-gray-400">Fill in the details below</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setNewTicket({ title: '', description: '', category: '', priority: 'medium' })
                  setNewTicketAttachments([])
                  setShowCreateForm(false)
                }}
                className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-gray-700 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Subject */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">
                  Subject <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Brief description of your issue"
                  className="w-full px-4 py-3 bg-gray-800/80 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-white placeholder-gray-500 transition-all"
                />
              </div>

              {/* Category Cards - replaces broken select dropdown */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">
                  Category {newTicket.category && <span className="text-yellow-400 font-normal ml-1">— {formatCategoryLabel(newTicket.category)}</span>}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    'general_support', 'account_finance', 'technical_faults',
                    'user_data_profile', 'commissions_referrals'
                  ].map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setNewTicket(prev => ({ ...prev, category: prev.category === cat ? '' : cat }))}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-center ${
                        newTicket.category === cat
                          ? 'border-yellow-500 bg-yellow-900/30 shadow-lg shadow-yellow-900/20 scale-[1.02]'
                          : 'border-gray-700 bg-gray-800/50 hover:border-gray-500 hover:bg-gray-800'
                      }`}
                    >
                      <span className="text-xl">{getCategoryIcon(cat)}</span>
                      <span className={`text-xs font-medium leading-tight ${
                        newTicket.category === cat ? 'text-yellow-300' : 'text-gray-300'
                      }`}>
                        {CATEGORY_CONFIG[cat]?.label || cat}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">Priority</label>
                <div className="flex gap-2">
                  {([
                    { value: 'low', label: 'Low', color: 'border-green-600 bg-green-900/30 text-green-300' },
                    { value: 'medium', label: 'Medium', color: 'border-yellow-600 bg-yellow-900/30 text-yellow-300' },
                    { value: 'high', label: 'High', color: 'border-orange-600 bg-orange-900/30 text-orange-300' },
                    { value: 'urgent', label: 'Urgent', color: 'border-red-600 bg-red-900/30 text-red-300' },
                  ] as const).map(p => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setNewTicket(prev => ({ ...prev, priority: p.value }))}
                      className={`flex-1 py-2 px-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                        newTicket.priority === p.value
                          ? `${p.color} scale-[1.02] shadow-md`
                          : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment-specific instructions */}
              {newTicket.category === 'bank_payment' && (
                <div className="p-3 bg-blue-900/50 border border-blue-500 rounded-lg">
                  <p className="text-sm font-medium text-blue-300 mb-2">📋 Required Information for Bank Payment Issues:</p>
                  <ul className="text-sm text-blue-200 space-y-1 list-disc list-inside">
                    <li>Reference number you <strong className="text-blue-100">should have used</strong></li>
                    <li>Reference number you <strong className="text-blue-100">actually used</strong></li>
                    <li>Amount paid (in ZAR)</li>
                    <li>Date of payment</li>
                  </ul>
                  <p className="text-sm text-yellow-400 mt-2 font-medium">⚠️ Please upload your proof of payment below!</p>
                </div>
              )}

              {newTicket.category === 'crypto_payment' && (
                <div className="p-3 bg-purple-900/50 border border-purple-500 rounded-lg">
                  <p className="text-sm font-medium text-purple-300 mb-2">📋 Required Information for Crypto Payment Issues:</p>
                  <ul className="text-sm text-purple-200 space-y-1 list-disc list-inside">
                    <li>Wallet address you sent <strong className="text-purple-100">from</strong></li>
                    <li>Wallet address you sent <strong className="text-purple-100">to</strong></li>
                    <li>Transaction hash (TxID)</li>
                    <li>Amount sent (in USD)</li>
                    <li>Cryptocurrency used (e.g., USDT, BTC)</li>
                    <li>Network/Chain (e.g., TRC20, ERC20, BEP20)</li>
                  </ul>
                  <p className="text-sm text-yellow-400 mt-2 font-medium">⚠️ Please upload a screenshot of the transaction below!</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">
                  Description {(newTicket.category === 'bank_payment' || newTicket.category === 'crypto_payment') && <span className="text-red-400">*</span>}
                </label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={
                    newTicket.category === 'bank_payment'
                      ? "Please include:\n• Reference you should have used: \n• Reference you actually used: \n• Amount paid: R\n• Date of payment: "
                      : newTicket.category === 'crypto_payment'
                      ? "Please include:\n• Wallet you sent from: \n• Wallet you sent to: \n• Transaction hash: \n• Amount: $\n• Crypto: \n• Network: "
                      : "Please provide detailed information about your issue"
                  }
                  rows={newTicket.category === 'bank_payment' || newTicket.category === 'crypto_payment' ? 6 : 4}
                  className="w-full px-4 py-3 bg-gray-800/80 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-white placeholder-gray-500 transition-all"
                />
              </div>

              {/* Image Attachments */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">
                  {(newTicket.category === 'bank_payment' || newTicket.category === 'crypto_payment')
                    ? <>Proof of Payment <span className="text-red-400">* (Required)</span></>
                    : 'Attach Images (Optional)'
                  }
                </label>
                <input
                  type="file"
                  ref={createFileInputRef}
                  onChange={handleNewTicketFileSelect}
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                  multiple
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => createFileInputRef.current?.click()}
                  className={`w-full px-4 py-3 border-2 border-dashed rounded-xl transition-all flex items-center justify-center gap-2 ${
                    (newTicket.category === 'bank_payment' || newTicket.category === 'crypto_payment')
                      ? 'border-yellow-500 bg-yellow-900/20 text-yellow-400 hover:border-yellow-400 hover:bg-yellow-900/40'
                      : 'border-gray-600 text-gray-400 hover:border-yellow-500 hover:text-yellow-400 hover:bg-gray-800'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {(newTicket.category === 'bank_payment' || newTicket.category === 'crypto_payment')
                    ? 'Upload Proof of Payment'
                    : 'Add Images'
                  }
                </button>
                <p className="text-xs text-gray-500 mt-1.5">Max 5MB per file. JPEG, PNG, GIF, WebP, PDF supported.</p>

                {/* Preview attached images */}
                {newTicketAttachments.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {newTicketAttachments.map((file, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={newTicketPreviewUrls[index] || ''}
                          alt={file.name}
                          className="w-16 h-16 object-cover rounded-xl border-2 border-gray-600 group-hover:border-yellow-500 transition-colors"
                        />
                        <button
                          type="button"
                          onClick={() => removeNewTicketAttachment(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 shadow-lg"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Ticket limit warning */}
              {openTicketCount >= MAX_OPEN_TICKETS && (
                <div className="p-3 bg-red-900/40 border border-red-600 rounded-xl">
                  <p className="text-sm text-red-300">
                    ⚠️ You have reached the maximum of {MAX_OPEN_TICKETS} open tickets. Please wait for existing tickets to be resolved.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setNewTicket({ title: '', description: '', category: '', priority: 'medium' })
                  setNewTicketAttachments([])
                  setShowCreateForm(false)
                }}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-xl transition-all border border-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTicket}
                disabled={!newTicket.title.trim() || loading || openTicketCount >= MAX_OPEN_TICKETS}
                className="flex-1 bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 text-white font-semibold py-3 px-4 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg"
              >
                {loading ? 'Creating...' : '🎫 Create Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <div className="text-center py-16 bg-gradient-to-b from-gray-800/60 to-gray-900/60 rounded-2xl border border-gray-700">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-yellow-500/20 to-yellow-700/20 rounded-2xl flex items-center justify-center mb-5">
            <span className="text-4xl">🎫</span>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">No Support Tickets</h3>
          <p className="text-gray-400 mb-6 max-w-sm mx-auto">You haven&apos;t created any support tickets yet. Need help? Create your first ticket.</p>
          <button
            onClick={() => {
              setNewTicket({ title: '', description: '', category: '', priority: 'medium' })
              setNewTicketAttachments([])
              setShowCreateForm(true)
            }}
            className="bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-lg inline-flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Your First Ticket
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Your Tickets ({tickets.length})</h3>
          </div>
          {tickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => { setSelectedTicket(ticket); setCsatRating(0); setCsatFeedback(''); setCsatSubmitted(false) }}
              className="bg-gray-800/60 hover:bg-gray-800 border border-gray-700 hover:border-gray-600 rounded-xl p-4 cursor-pointer transition-all group"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-gray-700/80 flex items-center justify-center flex-shrink-0 text-lg group-hover:bg-gray-700">
                    {getCategoryIcon(ticket.category || '')}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-gray-500">#{ticket.ticket_number}</span>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-md ${getStatusColor(ticket.status)}`}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-md ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                    </div>
                    <h4 className="text-sm font-semibold text-white truncate group-hover:text-yellow-300 transition-colors">
                      {ticket.title}
                    </h4>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-gray-500">{formatCategoryLabel(ticket.category || '')}</span>
                      <span className="text-xs text-gray-600">•</span>
                      <span className="text-xs text-gray-500">{formatDate(ticket.created_at)}</span>
                      <span className="text-xs text-gray-600">•</span>
                      <span className="text-xs text-gray-500">{ticket.agent?.name || 'Unassigned'}</span>
                    </div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-600 group-hover:text-yellow-500 transition-colors flex-shrink-0 mt-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-b from-gray-900 to-gray-950 border border-gray-700 rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            {/* Header */}
            <div className="p-5 border-b border-gray-700 flex-shrink-0">
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gray-700/80 flex items-center justify-center text-xl flex-shrink-0">
                    {getCategoryIcon(selectedTicket.category || 'general')}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-white">
                        #{selectedTicket.ticket_number}
                      </h3>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-md ${getStatusColor(selectedTicket.status)}`}>
                        {selectedTicket.status.replace('_', ' ')}
                      </span>
                      <span className={`inline-flex px-2 py-0.5 text-xs font-bold rounded-md ${getPriorityColor(selectedTicket.priority)}`}>
                        {selectedTicket.priority}
                      </span>
                    </div>
                    <p className="text-white text-sm font-medium">{selectedTicket.title}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatCategoryLabel(selectedTicket.category || 'general')} · {(selectedTicket.agent as any)?.agent_name || selectedTicket.agent?.name || 'Unassigned'} · {formatDate(selectedTicket.created_at)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedTicket(null)
                    setResponses([])
                    setReplyMessage('')
                    setReplyAttachments([])
                  }}
                  className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-gray-700 rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Conversation Thread */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-800/50 min-h-[300px] max-h-[50vh]">
              {/* Original Ticket Message */}
              <div className="bg-yellow-900/30 rounded-lg p-4 border-l-4 border-yellow-500">
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-8 h-8 bg-yellow-600 rounded-full flex items-center justify-center text-sm font-medium text-white">
                    You
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-white">You</div>
                    <div className="text-sm text-gray-500">{formatDate(selectedTicket.created_at)}</div>
                  </div>
                </div>
                <div className="text-sm text-gray-300 whitespace-pre-wrap ml-11">
                  {selectedTicket.description || '(No description provided)'}
                </div>
              </div>

              {/* Loading indicator */}
              {loadingResponses && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500"></div>
                </div>
              )}

              {/* Responses */}
              {responses.map(response => (
                <div
                  key={response.id}
                  className={`rounded-lg p-4 ${
                    response.responder_type === 'agent'
                      ? 'bg-blue-900/30 border-l-4 border-blue-500'
                      : 'bg-yellow-900/30 border-l-4 border-yellow-500'
                  }`}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium text-white ${
                      response.responder_type === 'agent' ? 'bg-blue-600' : 'bg-yellow-600'
                    }`}>
                      {response.responder_type === 'agent'
                        ? (response.responder?.agent_name?.[0]?.toUpperCase() || 'A')
                        : 'You'}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-white">
                        {response.responder_type === 'agent'
                          ? (response.responder?.agent_name || 'Support Agent')
                          : 'You'}
                      </div>
                      <div className="text-sm text-gray-500">{formatDate(response.created_at)}</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-300 whitespace-pre-wrap ml-11">
                    {response.message}
                  </div>

                  {/* Display attachments */}
                  {response.attachments && response.attachments.length > 0 && (
                    <div className="mt-3 ml-11 flex flex-wrap gap-2">
                      {response.attachments.map((attachment, idx) => {
                        const attachmentUrl = attachment.signedUrl || attachment.url
                        return (
                        <a
                          key={idx}
                          href={attachmentUrl?.startsWith('https://') ? attachmentUrl : '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                          onClick={(e) => { if (!attachmentUrl?.startsWith('https://')) e.preventDefault() }}
                        >
                          <img
                            src={attachmentUrl?.startsWith('https://') ? attachmentUrl : ''}
                            alt={attachment.name}
                            className="max-w-[200px] max-h-[150px] rounded-lg border border-gray-600 hover:border-yellow-500 transition-colors"
                          />
                        </a>
                      )})}
                    </div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Box - Only show if ticket is not closed/resolved */}
            {!['closed', 'resolved'].includes(selectedTicket.status) && (
              <div className="border-t border-gray-700 p-4 bg-gray-900 flex-shrink-0">
                <div className="space-y-3">
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply..."
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-white placeholder-gray-500 resize-none"
                    rows={3}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        handleSendReply()
                      }
                    }}
                  />

                  {/* Attachment preview */}
                  {replyAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {replyAttachments.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={replyPreviewUrls[index] || ''}
                            alt={file.name}
                            className="w-16 h-16 object-cover rounded-lg border border-gray-600"
                          />
                          <button
                            type="button"
                            onClick={() => removeReplyAttachment(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-sm hover:bg-red-600"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        ref={replyFileInputRef}
                        onChange={handleReplyFileSelect}
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                        multiple
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => replyFileInputRef.current?.click()}
                        className="px-3 py-2 text-gray-400 hover:text-yellow-500 hover:bg-gray-800 rounded-lg transition-colors flex items-center gap-1"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm">Attach File</span>
                      </button>
                      <span className="text-sm text-gray-500">Ctrl+Enter to send</span>
                    </div>
                    <button
                      onClick={handleSendReply}
                      disabled={(!replyMessage.trim() && replyAttachments.length === 0) || sendingReply}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-500 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                    >
                      {sendingReply ? 'Sending...' : 'Send Reply'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Closed/Resolved notice with CSAT */}
            {['closed', 'resolved'].includes(selectedTicket.status) && (
              <div className="border-t border-gray-700 p-4 bg-gray-800 flex-shrink-0">
                <p className="text-sm text-white text-center mb-3">
                  This ticket has been {selectedTicket.status}. If you need further assistance, please create a new ticket.
                </p>
                {!selectedTicket.satisfaction_rating && !csatSubmitted ? (
                  <div className="bg-gray-700 rounded-lg p-4 max-w-md mx-auto">
                    <p className="text-sm text-white text-center font-medium mb-2">How was your experience?</p>
                    <div className="flex justify-center gap-2 mb-3">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button key={star} onClick={() => setCsatRating(star)}
                          className={`text-2xl transition-transform hover:scale-125 ${star <= csatRating ? 'text-yellow-400' : 'text-gray-500'}`}>
                          ★
                        </button>
                      ))}
                    </div>
                    {csatRating > 0 && (
                      <>
                        <textarea value={csatFeedback} onChange={e => setCsatFeedback(e.target.value)}
                          placeholder="Any additional feedback? (optional)"
                          className="w-full px-3 py-2 bg-gray-600 border border-gray-500 text-white placeholder-gray-400 rounded text-sm mb-2" rows={2} />
                        <button onClick={submitCSAT} disabled={csatSubmitting}
                          className="w-full px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:bg-gray-500 text-sm font-medium">
                          {csatSubmitting ? 'Submitting...' : 'Submit Rating'}
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-sm text-green-400">
                    {csatSubmitted ? '✅ Thank you for your feedback!' : `⭐ You rated this ${selectedTicket.satisfaction_rating}/5`}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 max-w-sm">
          {toasts.map(toast => (
            <div
              key={toast.id}
              className={`flex items-start gap-3 p-4 rounded-lg shadow-xl border backdrop-blur-sm animate-[slideIn_0.3s_ease-out] ${
                toast.type === 'error' ? 'bg-red-900/90 border-red-600 text-red-100' :
                toast.type === 'warning' ? 'bg-amber-900/90 border-amber-600 text-amber-100' :
                toast.type === 'success' ? 'bg-green-900/90 border-green-600 text-green-100' :
                'bg-blue-900/90 border-blue-600 text-blue-100'
              }`}
            >
              <span className="text-lg flex-shrink-0 mt-0.5">
                {toast.type === 'error' ? '❌' : toast.type === 'warning' ? '⚠️' : toast.type === 'success' ? '✅' : 'ℹ️'}
              </span>
              <p className="text-sm flex-1">{toast.message}</p>
              <button
                onClick={() => dismissToast(toast.id)}
                className="text-white/60 hover:text-white flex-shrink-0 ml-1"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default TicketingSystem
